import { clsx } from "clsx";
import * as Flags from "country-flag-icons/react/1x1";

import { GlobeIcon } from "../../Icons";

/**
 * CountryFlag component that displays the flag of a given country code.
 *
 * Covers every ISO 3166-1 alpha-2 code via `country-flag-icons`. The hand-rolled
 * map over `Icons/flags/*` it replaced only had artwork for 23 countries, and the
 * report groups by TUNE's `country_code`, so any other country silently rendered
 * the globe fallback.
 *
 * The `1x1` variant is deliberate over `3x2` (which `PhoneInputV2` uses): its
 * viewBox is the flag's centre square, so it fills the round box with no
 * letterboxing.
 *
 * Codes are upper-cased before lookup: the Performance Report API returns TUNE's
 * `Stat.country_code` in upper case, while `partners_registry.country.code` is
 * `CITEXT`, so callers can't be relied on to agree on casing.
 *
 * @param param0 - An object containing the country code.
 * @param param0.code - The ISO 3166-1 alpha-2 country code.
 * @returns A JSX element representing the country flag or a globe icon if the flag is not available.
 */
export const CountryFlag = ({
  code,
  size = "sm",
}: {
  code?: string;
  /** `sm` (24px) for table rows; `md` (32px) for the mobile detail screen. */
  size?: "sm" | "md";
}) => {
  const Flag = code
    ? Flags[code.toUpperCase() as keyof typeof Flags]
    : undefined;
  const boxSize = clsx(size === "md" ? "h-8 w-8" : "h-6 w-6");
  // Hairline outline via an outward `ring` (box-shadow), not `border`: a sub-1px
  // `border-width` is snapped up by the browser (DevTools reports ~1.5px for
  // `border-[0.5px]`), and an *inset* ring would be painted over by the flag
  // artwork, which fills the whole box. An outward ring stays visible and adds
  // no layout size.
  const outline = "ring-[0.5px] ring-stroke-primary";

  if (Flag) {
    return (
      <Flag
        className={clsx(
          "flex shrink-0 overflow-hidden rounded-full",
          boxSize,
          outline,
        )}
        aria-hidden="true"
        focusable="false"
      />
    );
  }

  // Globe fallback scales with the box so it keeps the same optical inset.
  const globeSize = clsx(
    size === "md" ? "h-[18px] w-[18px]" : "h-[14px] w-[14px]",
  );

  return (
    <span
      className={clsx(
        "flex shrink-0 items-center justify-center rounded-full bg-surface-brand-subtle text-icon-brand-default",
        boxSize,
        outline,
      )}
    >
      <GlobeIcon className={globeSize} aria-hidden="true" focusable="false" />
    </span>
  );
};
